import * as R from 'ramda';
import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { SwitchField } from '../form';
import { LABEL_BEFORE } from '../form/switch';

const messages = defineMessages({
  displaySelected: {
    id: 'filter.selected',
    defaultMessage: 'Filter selected',
  },
});

const WithSelection = (config = {}) => WrappedComponent =>
  class MultiSelection extends React.PureComponent {
    constructor(props) {
      super(props);

      const {
        initialSelection = null,
        intitialDisplaySelected = false,
        mapItemToSelection = R.identity,
      } = R.is(Function, config) ? config(props) : config;

      this.mapItemToSelection = mapItemToSelection;
      this.selected = this.selected.bind(this);
      this.handleItemSelect = this.handleItemSelect.bind(this);
      this.handleSelectAll = this.handleSelectAll.bind(this);
      this.setDisplaySelected = this.setDisplaySelected.bind(this);
      this.hasSelection = this.hasSelection.bind(this);
      this.getItems = this.getItems.bind(this);
      this.getFilter = this.getFilter.bind(this);
      this.getSelection = this.getSelection.bind(this);
      this.setSelection = this.setSelection.bind(this);
      this.isAllSelected = this.isAllSelected.bind(this);
      this.isDisplaySelected = this.isDisplaySelected.bind(this);
      this.clear = this.clear.bind(this);

      this.state = {
        selection: initialSelection,
        displaySelected: intitialDisplaySelected,
      };

      if (props.multiSelectionRef) {
        props.multiSelectionRef(this);
      }
    }

    componentWillReceiveProps(props) {
      if (R.is(Function, config)) {
        const {
          initialSelection,
          intitialDisplaySelected,
        } = config(props);

        if (!this.state.selection) {
          this.setState({
            selection: initialSelection,
            displaySelected: intitialDisplaySelected,
          });
        }
      }
    }

    setDisplaySelected(checked) {
      this.setState({
        displaySelected: checked,
      });
    }

    getSelection() {
      return this.state.selection || [];
    }

    setSelection(selection) {
      return this.setState(state => ({
        selection: R.is(Array, selection)
          ? selection.map(this.mapItemToSelection)
          : state.selection,
      }));
    }

    getItems(items) {
      return this.isDisplaySelected()
        ? items.filter(this.selected)
        : items;
    }

    getFilter() {
      return (
        <SwitchField
          className="mdc-toolbar__text"
          id="input-display"
          label={<FormattedMessage {...messages.displaySelected} />}
          labelPosition={LABEL_BEFORE}
          checked={this.isDisplaySelected()}
          disabled={R.not(this.hasSelection())}
          onChange={e => this.setDisplaySelected(e.target.checked)}
        />
      );
    }

    isDisplaySelected() {
      return this.state.displaySelected;
    }

    selected(item) {
      return R.find(R.equals(this.mapItemToSelection(item)), this.getSelection());
    }

    isAllSelected(items = []) {
      return this.hasSelection() && R.is(Array, items)
        && this.getItems(items).length === this.getItems(items).filter(this.selected).length;
    }

    handleSelectAll(items) {
      return (evt) => {
        const allSelect = evt.target.checked;
        const toSelection = itms => (itms ? R.map(this.mapItemToSelection, itms) : []);
        this.setState(({ displaySelected, ...state }) => {
          const selection = state.selection || [];

          return {
            selection:
              allSelect
                ? selection.concat(
                  toSelection(items).filter(item => !R.contains(item, selection)),
                )
                : R.without(toSelection(items), selection),
            displaySelected: allSelect ? displaySelected : false,
          };
        });
      };
    }

    handleItemSelect(item) {
      const selection = this.getSelection();
      const selection$ = this.selected(item)
        ? R.reject(R.equals(this.mapItemToSelection(item)), selection)
        : R.append(this.mapItemToSelection(item), selection);

      this.setState(({ displaySelected }) => ({
        selection: selection$,
        displaySelected: R.isEmpty(selection$) ? false : displaySelected,
      }), this.forceUpdate);
    }

    hasSelection() {
      return R.not(R.isEmpty(this.getSelection()));
    }

    clear() {
      this.setState({
        selection: null,
        displaySelected: false,
      });
    }

    render() {
      return (
        <WrappedComponent
          {...this.props}
          multiSelection={{
            ...this,
          }}
        />
      );
    }
  };

export default WithSelection;
